import styled from "@emotion/styled";
import { ExSelect } from "@energyx-dev/energyx-ui";
import { TermsViewer } from "components/common/TermsViewer";
import { MoveTerms } from "components/layout/MoveTerms";
import { TermTypeTabs } from "components/terms/TermTypeTabs";
import { totalServices } from "components/terms/helper";
import { useFetchTermsHistory } from "hooks/queries/terms/termsQueries";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { TServices, TTempTermTypes, TTerms } from "types/terms/termsTypes";
import { MobileView } from "react-device-detect";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

const tempTermTypes: TTempTermTypes = {
  ENERGYX: [
    {
      label: "서비스 이용약관",
      value: "PRIVACY_USE_AGREEMENT",
    },
    {
      label: "개인정보 처리방침",
      value: "PRIVACY_POLICY",
    },
  ],
  TOMARCH: [
    {
      label: "서비스 이용약관",
      value: "PRIVACY_USE_AGREEMENT",
    },
    {
      label: "개인정보 처리방침",
      value: "PRIVACY_POLICY",
    },
  ],
  DESIGN_WHOS: [
    {
      label: "서비스 이용약관",
      value: "PRIVACY_USE_AGREEMENT",
    },
    {
      label: "개인정보 처리방침",
      value: "PRIVACY_POLICY",
    },
  ],
};

export const Terms = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const qsFetchfrom = (queryParams.get("fetchfrom") || "ENERGYX") as TServices;
  const qsTabValue = (queryParams.get("tabValue") || "PRIVACY_USE_AGREEMENT") as TTerms;

  const methods = useForm();
  const { watch, setValue } = methods;
  const termIdWatch = watch("termId");
  const [tempTermHistory, setTempTermHistory] = useState<{ label: string; value: number }[]>();
  const [params, setParams] = useState<{ fetchfrom: TServices; tabValue: TTerms }>({
    fetchfrom: qsFetchfrom,
    tabValue: qsTabValue,
  });
  const { fetchfrom, tabValue } = params;
  const currTermIndex = tempTermTypes["ENERGYX"].findIndex((item) => item.value === tabValue);
  const { data: termsHistories } = useFetchTermsHistory(tabValue, "ENERGYX");

  /*
  const handleChangeService = (i: number) => {
    const _fetchfrom = totalServices[i].value;
    setParams({ ...params, fetchfrom: _fetchfrom, tabValue: tempTermTypes[_fetchfrom][0].value });
  };
  */

  const handleOnChangeTab = (i: number) => {
    setParams({ ...params, tabValue: tempTermTypes[fetchfrom][i].value });
  };

  useEffect(() => {
    if (termsHistories?.termsHistories) {
      const _tempTermHistory = [
        ...termsHistories?.termsHistories.map((terms) => ({
          label: dayjs(terms.createdAt).format("YYYY-MM-DD"),
          value: terms.id,
        })),
      ];

      _tempTermHistory && setTempTermHistory(_tempTermHistory);
      setValue("termId", _tempTermHistory.length ? _tempTermHistory[0].value : 0);
    } else {
      setTempTermHistory([]);
      setValue("termId", 0);
    }
  }, [termsHistories]);

  return (
    <>
      <Section>
        <ServiceTabWrapper>
          <h5>전체 서비스</h5>
          <ServiceTabs>
            {totalServices.map((service, i) => {
              return (
                <li role="presentation" key={i} className={"active"}>
                  <img
                    className={service.logo_m ? "have_m_logo" : ""}
                    src={service.logo}
                    alt="서비스 로고"
                  />
                  {service.logo_m && (
                    <img className="m_logo" src={service.logo_m} alt="모바일 로고" />
                  )}
                </li>
              );
            })}
          </ServiceTabs>
        </ServiceTabWrapper>
        <div className="contents">
          <TermTypeTabs<TTerms>
            tabs={tempTermTypes[fetchfrom]}
            onTabChange={handleOnChangeTab}
            activeTabIndex={currTermIndex}
          />
          <TermTitle>
            <h3>{tempTermTypes[fetchfrom][currTermIndex].label}</h3>
            <FormProvider {...methods}>
              {tempTermHistory && (
                <ExSelect
                  name="termId"
                  options={tempTermHistory}
                  size="small"
                  variant="dropdown-bottom"
                  width={144}
                  placeholder="지난 약관 보기"
                />
              )}
            </FormProvider>
          </TermTitle>
          {!!termIdWatch && <TermsViewer termsId={termIdWatch} currentTermIndex={currTermIndex} />}
        </div>
      </Section>
      <MobileView style={{ width: "100%" }}>
        <MoveTerms />
      </MobileView>
    </>
  );
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  padding: 100px 0;
  gap: 100px;
  width: 100%;

  .contents {
    width: 720px;
    margin: 0 auto;
  }

  ${({ theme }) => theme.breakpoints.down("TABLET")} {
    padding: 40px 0 0;
    gap: 40px;

    .contents {
      margin: 0;
      max-width: 100%;
      .ck-content {
        padding: 0 16px 64px;
      }
    }
  }
`;

const ServiceTabWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  h5 {
    font-size: 16px;
    font-weight: 700;
    line-height: 25px;
    letter-spacing: -0.25px;
  }

  ${({ theme }) => theme.breakpoints.down("TABLET")} {
    padding: 0 16px;
  }
`;

const ServiceTabs = styled.ul`
  display: flex;
  align-items: center;

  li {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 360px;
    height: 60px;
    border-right: 1px solid #adadad;

    img {
      filter: grayscale(1);
      height: 19px;
      opacity: 0.5;
      transition: all 0.3s ease;

      &.m_logo {
        display: none;
      }
    }

    &.active {
      img {
        opacity: 1;
        filter: grayscale(0);
      }
    }

    :nth-of-type(2) {
      img {
        height: 38px;
      }
    }

    :last-of-type {
      border-right: none;
    }
  }

  ${({ theme }) => theme.breakpoints.down("TABLET")} {
    width: 100%;
    li {
      width: 100%;
      height: 20px;
      padding: 0 10px;

      img {
        &.have_m_logo {
          display: none;
        }

        &.m_logo {
          display: block;
        }
      }

      &:first-of-type {
        img {
          height: calc(((100vw - 32px - 2px - 60px) / 3) * 0.14);
        }
      }

      &:nth-of-type(2) {
        img {
          height: calc(((100vw - 32px - 2px - 60px) / 3) * 0.25);
        }
      }
      &:last-of-type {
        img {
          height: calc(((100vw - 32px - 2px - 60px) / 3) * 0.1);
        }
      }
    }
  }
`;

const TermTitle = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 100px 0 32px;

  h3 {
    font-size: 30px;
    font-weight: 800;
    line-height: 34px;
    letter-spacing: -0.25px;
  }

  ${({ theme }) => theme.breakpoints.down("TABLET")} {
    flex-direction: column-reverse;
    margin: 0;
    padding: 32px 16px;
    gap: 32px;
  }
`;
